// src/pages/guides/ExposedRDPGuide.js
// Exposed RDP Response Guide - SEO Playbook Page

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function ExposedRDPGuide() {
  const startTrial = useTrialCta('guide_trial:exposed-rdp');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Exposed RDP Detection: Find and Secure Port 3389",
    "description": "Detect exposed RDP (port 3389) before attackers do. EASM-powered discovery, immediate containment steps, and continuous monitoring to prevent recurrence.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2025-12-01T00:00:00-05:00",
    "dateModified": "2026-08-25T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/exposed-rdp",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/exposed-rdp",
    "keywords": ["exposed RDP detection", "find exposed RDP", "RDP port 3389 security", "RDP monitoring", "remote desktop exposure"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Exposed RDP Detection" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Exposed RDP Detection: Find & Secure Port 3389 | DriftAlarm"
        description="Detect exposed RDP (port 3389) before attackers do. EASM-powered discovery, immediate containment steps, and continuous monitoring to prevent recurrence."
        path="/guides/exposed-rdp"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/exposed-rdp')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Someone Exposed RDP to the Internet?!</h1>
          <GuideByline path="/guides/exposed-rdp" />
          <GuideAnswer path="/guides/exposed-rdp" />
          <p className="guide-hero-intro">
            Okay — deep breath. Remote Desktop (port 3389) open to the whole internet is one of those
            "wait, how did this happen" findings, and the clock is already running. The good news: the
            first moves are straightforward if you do them in the right order. Below is exactly how we'd
            work it — get it off the internet, figure out whether anyone actually got in, and don't guess
            when you can just check the logs.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What To Do, In Order</div>
            <ul>
              <li>Take it off the internet — without pulling the box offline</li>
              <li>Snapshot and preserve the logs before you touch anything</li>
              <li>Read the logs for who actually logged in</li>
              <li>Hunt for RDP sessions you can't account for</li>
              <li>Know when it's a recovery, not a cleanup</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#discovering-rdp">Discovering Exposed RDP</a></li>
            <li><a href="#why-critical">Why This Is Critical</a></li>
            <li><a href="#attacker-perspective">The Attacker Perspective</a></li>
            <li><a href="#immediate-response">First Moves (the first few hours)</a></li>
            <li><a href="#investigation">Did Anyone Get In? — Investigation</a></li>
            <li><a href="#remediation">Remediation Options</a></li>
            <li><a href="#prevention">Prevention Controls</a></li>
            <li><a href="#continuous-monitoring">Continuous RDP Monitoring</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="discovering-rdp" className="guide-section">
          <h2>Discovering Exposed RDP Before Attackers Do</h2>
          <p>
            The most dangerous exposed RDP instances are the ones you don't know about. Shadow IT,
            cloud misconfigurations, and forgotten test environments regularly expose port 3389
            without anyone on the security team being aware.
          </p>
          <p>
            External Attack Surface Management (EASM) takes an outside-in approach — scanning your
            IP ranges and domains the same way an attacker would, continuously discovering exposed
            services including RDP. Unlike one-time audits, EASM provides ongoing visibility into
            what's reachable from the internet.
          </p>
          <div className="callout callout-info">
            <div className="callout-title">Detection Methods for Exposed RDP</div>
            <div className="callout-content">
              <ul>
                <li><strong>Port scanning:</strong> Regular scans of your IP ranges for open port 3389 (and non-standard RDP ports)</li>
                <li><strong>Service banner analysis:</strong> Identifying RDP services even on non-standard ports</li>
                <li><strong>Configuration drift monitoring:</strong> Alerting when a previously closed port opens — a key indicator of misconfiguration</li>
                <li><strong>Asset inventory correlation:</strong> Flagging RDP on systems where it shouldn't exist</li>
              </ul>
            </div>
          </div>
          <p>
            The critical advantage of continuous monitoring over periodic assessments is <strong>time to detection</strong>.
            An RDP service exposed by a firewall change on Tuesday gets discovered by automated scanners within minutes —
            waiting until the next quarterly audit means months of undetected exposure.
          </p>
        </section>

        <section id="why-critical" className="guide-section">
          <h2>Why Exposed RDP Is Critical</h2>

          <div className="callout callout-critical">
            <div className="callout-title">Instant P1 Finding</div>
            <div className="callout-content">
              <p>
                <strong>RDP exposed to the internet without VPN, allowlist, or MFA gateway is an
                automatic P1.</strong> No scoring required. This finding demands action within 72 hours
                maximum—preferably within hours.
              </p>
            </div>
          </div>

          <p>
            RDP is the most commonly exploited remote access protocol in enterprise breaches. Here's why:
          </p>
          <ul>
            <li><strong>Credential attacks work:</strong> Brute force and credential stuffing against RDP
                succeed constantly because users reuse passwords</li>
            <li><strong>Known vulnerabilities:</strong> BlueKeep (CVE-2019-0708), DejaBlue, and other RDP
                vulnerabilities enable unauthenticated remote code execution</li>
            <li><strong>Ransomware entry point:</strong> Compromised RDP access is among the most common
                initial access vectors for ransomware attacks</li>
            <li><strong>No authentication logging by default:</strong> Failed login attempts often aren't
                logged or monitored</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">Attackers Are Scanning Right Now</div>
            <div className="callout-content">
              <p>
                Mass internet scanning tools like Shodan, Censys, and attacker-operated botnets continuously
                scan the entire IPv4 space for open port 3389. <strong>Within minutes of exposing RDP,
                your server will be discovered and targeted.</strong>
              </p>
              <p>
                This isn't theoretical—it's happening 24/7. Every exposed RDP endpoint receives brute-force
                attempts within hours of exposure.
              </p>
            </div>
          </div>
        </section>

        <section id="attacker-perspective" className="guide-section">
          <h2>The Attacker Perspective</h2>
          <p>
            Understanding how attackers exploit exposed RDP helps you prioritize your response:
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Most Likely, It's Credential Replay — Not a Zero-Day</div>
            <div className="callout-content">
              <p>
                As long as the box has been getting its Windows updates, the likely play here usually
                <strong> isn't a fancy exploit — it's credential replay</strong>. Attackers feed in
                username/password pairs harvested from years of old breach dumps and let automation grind
                through the list. Time is entirely on their side: they'll keep trying combinations until
                one works, and against RDP, one working password is all it takes.
              </p>
              <p>
                That's exactly why the whole investigation below comes down to a single question —
                <strong> did any of those attempts actually succeed?</strong> You answer it from the logs,
                not from a gut feeling.
              </p>
            </div>
          </div>

          <h3>Discovery (Minutes)</h3>
          <ul>
            <li>Automated scanners detect new RDP endpoints within minutes to hours</li>
            <li>RDP access is actively traded on criminal marketplaces at low cost</li>
            <li>Initial access brokers specialize in harvesting and reselling RDP credentials</li>
          </ul>

          <h3>Initial Access (Hours to Days)</h3>
          <ul>
            <li><strong>Credential stuffing:</strong> Testing leaked username/password combinations</li>
            <li><strong>Brute force:</strong> Automated password guessing (common passwords, variations)</li>
            <li><strong>Exploit kits:</strong> Automated exploitation of unpatched RDP vulnerabilities</li>
            <li><strong>Purchased credentials:</strong> Using credentials from other breaches</li>
          </ul>

          <h3>Post-Compromise (Hours)</h3>
          <ul>
            <li>Establish persistence (new accounts, scheduled tasks, services)</li>
            <li>Disable security controls (antivirus, EDR)</li>
            <li>Lateral movement to other systems</li>
            <li>Data exfiltration</li>
            <li>Ransomware deployment</li>
          </ul>

          <div className="example-box">
            <div className="example-title">Typical Attack Timeline</div>
            <div className="example-content">
              <p>
                A representative attack progression: RDP exposed → first brute-force attempts within minutes to hours →
                successful login (often via weak or reused password) → ransomware deployment within hours to a day →
                business discovers encryption the following morning.
              </p>
              <p>
                <strong>Total time from exposure to ransomware: often less than 24 hours.</strong>
              </p>
            </div>
          </div>
        </section>

        <section id="immediate-response" className="guide-section">
          <h2>First Moves (the first few hours)</h2>
          <p>
            Work these in order. The goal is to stop the bleeding <em>and</em> keep enough evidence around
            to answer the only question that matters: did anyone get in?
          </p>

          <h3>Step 1: Take It Off the Internet — But Keep the Box Online</h3>
          <div className="callout callout-info">
            <div className="callout-title">Block the Traffic, Don't Pull the Plug</div>
            <div className="callout-content">
              <p>
                First, take it off the internet by <strong>blocking inbound 3389 at the firewall</strong>.
                Do this at the network edge if you can:
              </p>
              <ul>
                <li>Perimeter firewall (preferred — blocks before traffic ever reaches the host)</li>
                <li>Cloud security group / NSG (for cloud workloads)</li>
                <li>Host-based firewall (Windows Firewall) as a backup layer</li>
              </ul>
              <p>
                <strong>Leave the machine running.</strong> You'll need it online to investigate whether
                anyone got in — shutting it down or reimaging it now destroys the volatile evidence and
                answers nothing. Blocking the port at the firewall already cuts off the attacker; you don't
                have to take the box down to do it.
              </p>
            </div>
          </div>
          <p>
            Then confirm the block actually took: scan port 3389 from an external IP to verify it no longer
            responds, check that legitimate internal/VPN access still works, and note the change.
          </p>

          <h3>Step 2: Snapshot and Preserve the Logs — Before You Touch Anything</h3>
          <div className="callout callout-warning">
            <div className="callout-title">Preserve First, Investigate Second</div>
            <div className="callout-content">
              <p>
                Before you start poking around, <strong>get a backup</strong>. If it's a VM, take a
                snapshot right now — it's the fastest way to freeze the machine's exact state. Otherwise,
                grab a backup of the system.
              </p>
              <p>
                Then <strong>preserve the event logs off the box</strong> while you can. Export the Windows
                Security log (and the RDP operational logs below) to another location. Logs roll and
                overwrite themselves — you don't want your own investigation, or the attacker, to age out
                the exact records you need. Also jot down the <strong>time window</strong> RDP was exposed,
                so you know which slice of the logs to focus on.
              </p>
            </div>
          </div>

          <h3>Step 3: Read the Logs for Successful Logins</h3>
          <p>
            Now answer the question. Open the preserved Security log and look at the exposure window for
            <strong> successful</strong> remote logins — not just the failures:
          </p>
          <ul>
            <li><strong>Event ID 4624, Logon Type 10</strong> — a successful remote interactive (RDP) login. This is the one that tells you someone got in.</li>
            <li><strong>Event ID 4625</strong> — failed logins. Expect a flood of these from brute-force/credential-replay noise; that alone is normal for an exposed box. It's the 4624s you care about.</li>
            <li><strong>Event ID 4648</strong> — logons using explicit credentials, which can flag credential reuse.</li>
            <li>For every successful login, note the <strong>account</strong>, the <strong>timestamp</strong>, and the <strong>source IP</strong>.</li>
          </ul>

          <h3>Step 4: Hunt for the RDP Sessions</h3>
          <p>
            Cross-check the login events against the actual RDP session history so nothing hides in the gaps:
          </p>
          <ul>
            <li>Check current and disconnected sessions with <strong><code>query user</code></strong> (a.k.a. <strong><code>quser</code></strong>) and <strong><code>qwinsta</code></strong>.</li>
            <li>Review the RDP operational logs — <strong>RemoteConnectionManager</strong> (Event ID <strong>1149</strong>, "user authentication succeeded" with the source) and <strong>LocalSessionManager</strong> (Event IDs <strong>21 / 25</strong>, session logon and reconnect).</li>
            <li>Line up each session's <strong>source IP and account</strong> with the 4624 events from Step 3 — a session you can see in one log but can't explain in the other is a red flag.</li>
          </ul>
          <div className="callout callout-critical">
            <div className="callout-title">If You Can't Vouch for a Session, Assume the Worst</div>
            <div className="callout-content">
              <p>
                Walk the successful logins and sessions past the actual owner of the box.
                <strong> Any session you can't validate with them — an unfamiliar account, an unexpected
                source IP, a login at 3 a.m. nobody remembers — assume the worst and begin a recovery
                process.</strong> Don't try to "clean" a machine you can't prove is clean. Once you know
                someone got in, rebuild from known-good rather than chasing artifacts.
              </p>
            </div>
          </div>
          <p>
            And once containment is done, loop in the people who need to know — your security team and IT
            leadership — and keep the timeline you've built in case it's needed for compliance reporting.
          </p>
        </section>

        <section id="investigation" className="guide-section">
          <h2>Investigation Checklist</h2>
          <p>
            After containing the exposure, investigate whether compromise occurred:
          </p>

          <h3>Authentication Analysis</h3>
          <ul>
            <li>Review failed login attempts (Event ID 4625) for volume and patterns</li>
            <li>Check successful logins (Event ID 4624, Logon Type 10) during exposure window</li>
            <li>Look for logins from unexpected geographic locations or IP ranges</li>
            <li>Identify any new user accounts created during exposure</li>
          </ul>

          <h3>System Integrity</h3>
          <ul>
            <li>Check for new scheduled tasks or services</li>
            <li>Review startup programs and Run keys</li>
            <li>Look for unauthorized software installations</li>
            <li>Check for disabled security tools</li>
            <li>Scan for malware with updated signatures</li>
          </ul>

          <h3>Lateral Movement Indicators</h3>
          <ul>
            <li>Review authentication logs on domain controllers</li>
            <li>Check for unusual SMB/RPC connections from the exposed host</li>
            <li>Look for credential harvesting tools (Mimikatz, etc.)</li>
            <li>Review network connections for command-and-control patterns</li>
          </ul>

          <div className="callout callout-critical">
            <div className="callout-title">When It's a Recovery, Not a Cleanup</div>
            <div className="callout-content">
              <p>
                The moment you can't attribute a successful login or session to a real, expected user,
                stop treating this as a cleanup and start a formal recovery. Escalate to incident response
                if you find any of:
              </p>
              <ul>
                <li>Successful logins from unknown IPs during the exposure window</li>
                <li>Sessions the owner can't account for</li>
                <li>New user accounts or group membership changes</li>
                <li>Evidence of malware or unauthorized tools</li>
                <li>Signs of lateral movement to other systems</li>
                <li>Any data exfiltration indicators</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="remediation" className="guide-section">
          <h2>Remediation Options</h2>
          <p>
            After immediate containment, implement proper remote access controls:
          </p>

          <h3>Option 1: VPN-Only Access (Recommended)</h3>
          <p>
            Require VPN connection before RDP access is possible:
          </p>
          <ul>
            <li>RDP only accessible from internal network or VPN</li>
            <li>VPN requires MFA for authentication</li>
            <li>VPN logs provide audit trail of remote access</li>
            <li>Single point of control for remote access policy</li>
          </ul>

          <h3>Option 2: Remote Desktop Gateway</h3>
          <p>
            Use Windows Remote Desktop Gateway as a secure proxy:
          </p>
          <ul>
            <li>RD Gateway terminates HTTPS (443) from internet</li>
            <li>Proxies RDP connections to internal servers</li>
            <li>Supports MFA integration</li>
            <li>Provides centralized logging and policy</li>
          </ul>

          <h3>Option 3: Zero Trust Network Access (ZTNA)</h3>
          <p>
            Modern approach using identity-aware proxies:
          </p>
          <ul>
            <li>No direct network access—all connections proxied</li>
            <li>Continuous authentication and authorization</li>
            <li>Device posture checking</li>
            <li>Examples: Cloudflare Access, Zscaler Private Access, Tailscale</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">MFA Is Non-Negotiable</div>
            <div className="callout-content">
              <p>
                <strong>Any remote access solution must require multi-factor authentication.</strong>
                Passwords alone are not sufficient—they are routinely compromised through phishing,
                credential stuffing, and breaches.
              </p>
              <p>
                NLA (Network Level Authentication) alone is NOT MFA. You need a true second factor.
              </p>
            </div>
          </div>

          <h3>What NOT to Do</h3>
          <ul>
            <li><strong>Don't rely on "security through obscurity"</strong>—changing the RDP port from
                3389 to another port does not provide security. Attackers scan all ports.</li>
            <li><strong>Don't use IP allowlists alone</strong>—they're helpful but not sufficient as
                a primary control. IPs can be spoofed or attackers can pivot through allowed IPs.</li>
            <li><strong>Don't disable NLA</strong>—it provides pre-authentication that reduces attack
                surface, even if it's not a complete solution.</li>
          </ul>
        </section>

        <section id="prevention" className="guide-section">
          <h2>Prevention Controls</h2>
          <p>
            Prevent RDP exposure from recurring:
          </p>

          <h3>Technical Controls</h3>
          <ul>
            <li><strong>Firewall rules:</strong> Explicit deny for 3389 from internet at perimeter</li>
            <li><strong>Cloud security groups:</strong> Default-deny for RDP in all cloud environments</li>
            <li><strong>Configuration management:</strong> Enforce RDP restrictions via Group Policy</li>
            <li><strong>Attack surface monitoring:</strong> Continuous scanning to detect new exposures</li>
          </ul>

          <h3>Process Controls</h3>
          <ul>
            <li><strong>Change management:</strong> Require security review for any firewall changes</li>
            <li><strong>Provisioning templates:</strong> Ensure new servers deploy with RDP blocked by default</li>
            <li><strong>Regular audits:</strong> Quarterly review of all remote access configurations</li>
            <li><strong>Incident playbook:</strong> Document response procedures for future exposures</li>
          </ul>

          <h3>Detection Controls</h3>
          <ul>
            <li><strong>External scanning:</strong> Daily scans of your IP ranges for exposed management ports</li>
            <li><strong>Alerting:</strong> Immediate notification when port 3389 becomes externally reachable</li>
            <li><strong>Authentication monitoring:</strong> Alert on failed RDP authentication patterns</li>
            <li><strong>Threat intelligence:</strong> Monitor for your IPs appearing in RDP marketplace listings</li>
          </ul>
        </section>

        <section id="continuous-monitoring" className="guide-section">
          <h2>How DriftAlarm Detects Exposed RDP</h2>
          <p>
            DriftAlarm's continuous monitoring platform scans your external attack surface for exposed RDP
            and other dangerous services. When port 3389 appears on any of your monitored assets, you receive
            an immediate drift alert — not days or weeks later, but as soon as the next scan cycle detects the change.
          </p>
          <p>
            The drift detection engine compares each scan against your asset's baseline. If RDP wasn't
            exposed yesterday but is today, that <Link to="/guides/configuration-drift">configuration drift</Link> triggers
            an alert with full context: which IP, when it changed, and what service is now visible.
          </p>
          <div className="callout callout-info">
            <div className="callout-title">Stuck? We Can Help You Work Through It</div>
            <div className="callout-content">
              <p>
                If you hit something you can't validate on your own — a session you can't explain, evidence
                you're not sure how to read, or you just want a second set of eyes on the response — email
                us at <a href="mailto:support@driftalarm.com">support@driftalarm.com</a> and we can help
                work through it with you.
              </p>
            </div>
          </div>
          <div className="guide-cta-section">
            <h3>How DriftAlarm Helps</h3>
            <p>Continuous external scanning detects exposed RDP within hours of misconfiguration — before attackers can exploit it. Drift alerts notify your team instantly when new ports open.</p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            RDP exposure is often found alongside other attack surface issues. Review these related guides:
          </p>
          <ul>
            <li>
              <Link to="/guides/configuration-drift">Configuration Drift Guide</Link> — Understand how security configurations change over time and why drift detection catches exposures that point-in-time scans miss.
            </li>
            <li>
              <Link to="/guides/exposed-ssh">Exposed SSH Guide</Link> — SSH (port 22) is another commonly exposed remote access protocol that requires similar containment and hardening approaches.
            </li>
            <li>
              <Link to="/guides/exposed-database-ports">Exposed Database Ports Guide</Link> — MongoDB, Redis, and PostgreSQL get accidentally exposed through similar misconfigurations as RDP.
            </li>
            <li>
              <Link to="/guides/exposed-admin-panels">Exposed Admin Panels Guide</Link> — Web-based management interfaces like cPanel, Jenkins, and database UIs are frequently found exposed and require similar immediate action.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — Learn how to continuously monitor your perimeter to detect RDP and other dangerous exposures before attackers do.
            </li>
            <li>
              <Link to="/exposed-rdp-monitoring">Exposed RDP Monitoring Solution</Link> — See how DriftAlarm continuously monitors for exposed RDP across your attack surface with automated drift alerts.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary: RDP Response Checklist</h2>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Timeframe</th>
                  <th>Action</th>
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>0-30 min</td>
                  <td>Block port 3389 from internet at firewall</td>
                  <td>Network/Security</td>
                </tr>
                <tr>
                  <td>30-60 min</td>
                  <td>Verify block, preserve logs, notify stakeholders</td>
                  <td>Security</td>
                </tr>
                <tr>
                  <td>1-4 hours</td>
                  <td>Investigate for signs of compromise</td>
                  <td>Security/IR</td>
                </tr>
                <tr>
                  <td>1-7 days</td>
                  <td>Implement proper remote access (VPN + MFA)</td>
                  <td>IT/Security</td>
                </tr>
                <tr>
                  <td>Ongoing</td>
                  <td>Monitor for re-exposure, enforce prevention controls</td>
                  <td>Security</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path="/guides/exposed-rdp" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default ExposedRDPGuide;
